import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ItemManagement = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    branch: "",
    category: "",
    search: "",
    startDate: "",
    endDate: "",
    specificDate: "",
    monthYear: "",
    reserve_for_branch: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    size: "",
    brand: "",
    uniquesize: "",
    ampere: "",
    position: "",
    origin: "",
    categoryId: "",  // CHANGED: from 'category' to 'categoryId'
    branch: "Lehulu",
    quantity: 0,
    reserve_for_branch: "",
  });
  const [editMode, setEditMode] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(null);
  const [buttonLoading, setButtonLoading] = useState({
    fetch: false,
    submit: false,
    edit: {},
    delete: {},
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showFilter, setShowFilter] = useState(false);

  // List of all branches
  const branches = ["Lehulu", "Kalkidan", "Store"];

  // Fetch all items and categories
  const fetchData = async () => {
    try {
      setButtonLoading((prev) => ({ ...prev, fetch: true }));
      const [itemsRes, categoriesRes] = await Promise.all([
        axios.get("https://backend.lehuluet.com/api/items/items"),
        axios.get("https://backend.lehuluet.com/api/categories/categories"),
      ]);
      setItems(itemsRes.data.data);
      setCategories(categoriesRes.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error fetching data");
    } finally {
      setButtonLoading((prev) => ({ ...prev, fetch: false }));
      setLoading(false);
    }
  };

  // Filtered items logic
  const filteredItems = items.filter((item) => {
    const itemDate = new Date(item.created_at);
    const itemDateAtMidnight = new Date(
      itemDate.getFullYear(),
      itemDate.getMonth(),
      itemDate.getDate()
    );

    const branchMatch = filters.branch === "" || item.branch === filters.branch;
    // FIXED: Use item.category?.id instead of item.category?._id
    const categoryMatch =
      filters.category === "" ||
      (item.category && item.category.id === parseInt(filters.category));
    const searchMatch =
      filters.search === "" ||
      (item.name &&
        item.name.toLowerCase().includes(filters.search.toLowerCase())) ||
      (item.brand &&
        item.brand.toLowerCase().includes(filters.search.toLowerCase()));
    const reserveBranchMatch =
      filters.reserve_for_branch === "" ||
      item.reserve_for_branch === filters.reserve_for_branch;

    let dateRangeMatch = true;
    if (filters.startDate || filters.endDate) {
      const startDate = filters.startDate
        ? new Date(new Date(filters.startDate).setHours(0, 0, 0, 0))
        : null;
      const endDate = filters.endDate
        ? new Date(new Date(filters.endDate).setHours(23, 59, 59, 999))
        : null;
      dateRangeMatch =
        (!startDate || itemDateAtMidnight >= startDate) &&
        (!endDate || itemDateAtMidnight <= endDate);
    }

    let specificDateMatch = true;
    if (filters.specificDate) {
      const specificDate = new Date(filters.specificDate);
      specificDateMatch =
        itemDateAtMidnight.getFullYear() === specificDate.getFullYear() &&
        itemDateAtMidnight.getMonth() === specificDate.getMonth() &&
        itemDateAtMidnight.getDate() === specificDate.getDate();
    }

    let monthYearMatch = true;
    if (filters.monthYear) {
      const [year, month] = filters.monthYear.split("-");
      monthYearMatch =
        itemDate.getFullYear() === parseInt(year) &&
        itemDate.getMonth() === parseInt(month) - 1;
    }

    return (
      branchMatch &&
      categoryMatch &&
      searchMatch &&
      reserveBranchMatch &&
      dateRangeMatch &&
      specificDateMatch &&
      monthYearMatch
    );
  });

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission - FIXED
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setButtonLoading((prev) => ({ ...prev, submit: true }));
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found. Please log in again.");
      }

      const submitData = { ...formData };
      
      // FIXED: Use categoryId (already in formData now)
      const selectedCategory = categories.find(
        (cat) => cat.id === parseInt(submitData.categoryId)
      );
      
      if (selectedCategory?.name.toLowerCase() !== "battery") {
        delete submitData.ampere;
        delete submitData.position;
      }

      // Only include reserve_for_branch if branch is Store and a valid branch is selected
      if (submitData.branch !== "Store" || !["Lehulu", "Kalkidan"].includes(submitData.reserve_for_branch)) {
        delete submitData.reserve_for_branch;
      }

      if (editMode) {
        // In edit mode, update only the specific item
        delete submitData.quantity;
        await axios.put(
          `https://backend.lehuluet.com/api/items/items/${currentItemId}`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        toast.success("Item updated successfully");
      } else {
        // In add mode, handle branch logic
        if (submitData.branch === "all") {
          // Validate quantity
          const quantity = Number(submitData.quantity);
          if (isNaN(quantity) || quantity < 0) {
            throw new Error("Quantity must be a valid non-negative number");
          }

          // Split quantity across branches
          const quantityPerBranch = Math.floor(quantity / 3);
          const addPromises = [];

          // Add for Lehulu and Kalkidan (no reserve_for_branch)
          ["Lehulu", "Kalkidan"].forEach((branch) => {
            const branchData = { ...submitData, branch, quantity: quantityPerBranch };
            delete branchData.reserve_for_branch;
            addPromises.push(
              axios.post(
                "https://backend.lehuluet.com/api/items/items",
                branchData,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              )
            );
          });

          // Add one item for Store
          const storeData = {
            ...submitData,
            branch: "Store",
            quantity: quantity - 2 * quantityPerBranch,
          };
          delete storeData.reserve_for_branch;
          addPromises.push(
            axios.post(
              "https://backend.lehuluet.com/api/items/items",
              storeData,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            )
          );

          await Promise.all(addPromises);
          toast.success("Items added to all branches successfully");
        } else {
          // Add item to single branch
          if (submitData.branch !== "Store" || !["Lehulu", "Kalkidan"].includes(submitData.reserve_for_branch)) {
            delete submitData.reserve_for_branch;
          }
          await axios.post(
            "https://backend.lehuluet.com/api/items/items",
            submitData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          toast.success("Item created successfully");
        }
      }
      resetForm();
      fetchData();
      setIsModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Error saving item");
    } finally {
      setButtonLoading((prev) => ({ ...prev, submit: false }));
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      size: "",
      brand: "",
      uniquesize: "",
      ampere: "",
      position: "",
      origin: "",
      categoryId: "",  // CHANGED
      branch: "Lehulu",
      quantity: 0,
      reserve_for_branch: "",
    });
    setEditMode(false);
    setCurrentItemId(null);
  };

  // Edit item - FIXED
  const handleEdit = (item) => {
    setFormData({
      name: item.name || "",
      size: item.size || "",
      brand: item.brand || "",
      uniquesize: item.uniquesize || "",
      ampere: item.ampere || "",
      position: item.position || "",
      origin: item.origin || "",
      categoryId: item.category?.id || "",  // CHANGED: use item.category.id
      branch: item.branch,
      quantity: item.quantity,
      reserve_for_branch: item.reserve_for_branch || "",
    });
    setEditMode(true);
    setCurrentItemId(item.id);  // CHANGED: use item.id instead of item._id
    setIsModalOpen(true);
  };

  // Delete item - FIXED
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        setButtonLoading((prev) => ({
          ...prev,
          delete: { ...prev.delete, [id]: true },
        }));

        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found. Please log in again.");
        }

        await axios.delete(`https://backend.lehuluet.com/api/items/items/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        toast.success("Item deleted successfully");
        fetchData();
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || error.message || "Error deleting item";
        toast.error(errorMessage);
      } finally {
        setButtonLoading((prev) => ({
          ...prev,
          delete: { ...prev.delete, [id]: false },
        }));
      }
    }
  };

  // View item
  const handleView = (item) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };

  // Handle opening add new item modal
  const handleAddNew = () => {
    resetForm();
    setIsModalOpen(true);
  };

  // Initialize
  useEffect(() => {
    fetchData();
  }, []);

  // Export to CSV
  const exportToCSV = () => {
    if (!filteredItems.length) return;

    const headers = [
      "Name",
      "Brand",
      "Size",
      "Category",
      "Branch",
      "Reserve for Branch",
      "Quantity",
    ];
    const rows = filteredItems.map((item) => [
      item.name || "",
      item.brand || "",
      item.size || "",
      item.category?.name || "",
      item.branch || "",
      item.reserve_for_branch || "",
      item.quantity || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "inventory_items.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
          <h1 className="text-3xl font-bold text-teal-700">Inventory Management</h1>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={handleAddNew}
              className="w-full sm:w-auto px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
            >
              Add New Item
            </button>
            <button
              onClick={() => setShowFilter(!showFilter)}
              className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              {showFilter ? "Hide Filters" : "Show Filters"}
            </button>
            <button
              onClick={exportToCSV}
              className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Export to CSV
            </button>
          </div>
        </div>

        {showFilter && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Filter Items</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="branch-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Branch
                </label>
                <select
                  id="branch-filter"
                  name="branch"
                  value={filters.branch}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All Branches</option>
                  <option value="Lehulu">Lehulu</option>
                  <option value="Kalkidan">Kalkidan</option>
                  <option value="Store">Store</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="reserve_for_branch-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Reserve for Branch
                </label>
                <select
                  id="reserve_for_branch-filter"
                  name="reserve_for_branch"
                  value={filters.reserve_for_branch}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All Branches</option>
                  <option value="Lehulu">Lehulu</option>
                  <option value="Kalkidan">Kalkidan</option>
                  <option value="Store">Store</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="category-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Category
                </label>
                <select
                  id="category-filter"
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>  {/* FIXED: use category.id */}
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="search-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Search
                </label>
                <input
                  type="text"
                  id="search-filter"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search items..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label
                  htmlFor="startDate"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Start Date
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label
                  htmlFor="endDate"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  End Date
                </label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label
                  htmlFor="specificDate"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Specific Date
                </label>
                <input
                  type="date"
                  id="specificDate"
                  name="specificDate"
                  value={filters.specificDate}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label
                  htmlFor="monthYear"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Month/Year
                </label>
                <input
                  type="month"
                  id="monthYear"
                  name="monthYear"
                  value={filters.monthYear}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-teal-700">Inventory Items</h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500 mr-2">
              {filteredItems.length} {filteredItems.length === 1 ? "item" : "items"} found
            </span>
            <button
              onClick={fetchData}
              className="p-1 text-teal-600 hover:text-teal-800 focus:outline-none"
              disabled={buttonLoading.fetch}
            >
              {buttonLoading.fetch ? (
                <svg
                  className="animate-spin h-5 w-5 text-teal-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No items found.{" "}
            {filters.branch || filters.category || filters.search
              ? "Try changing your filters."
              : "Create one to get started."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Brand
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unique Size / Amp
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position / Origin
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reserve for Branch
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((item) => (
                  <tr key={item.id}>  {/* FIXED: use item.id */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {item.category?.name}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{item.brand || "-"}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{item.size || "-"}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {item.uniquesize || item.ampere || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {item.position || item.origin}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="px-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-teal-100 text-teal-800">
                        {item.branch}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="px-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-teal-100 text-teal-800">
                        {item.reserve_for_branch || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-1 inline-flex text-xs leading-4 font-semibold rounded-full ${
                          item.quantity > 0
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {item.quantity}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleView(item)}
                        className="text-green-600 hover:text-green-900 mr-2"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-teal-600 hover:text-teal-900 mr-2"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}  // FIXED: use item.id
                        className="text-red-600 hover:text-red-900"
                        disabled={buttonLoading.delete[item.id]}
                      >
                        {buttonLoading.delete[item.id] ? (
                          <span className="flex items-center">
                            <svg
                              className="animate-spin -ml-1 mr-1 h-3 w-3 text-red-600"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Deleting...
                          </span>
                        ) : (
                          "Delete"
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit/Add Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-700">
                {editMode ? "Edit Item" : "Add New Item"}
              </h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  if (!editMode) resetForm();
                }}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label
                    htmlFor="categoryId"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Category*
                  </label>
                  <select
                    id="categoryId"
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>  {/* FIXED: use category.id */}
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="branch"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Branch*
                  </label>
                  <select
                    id="branch"
                    name="branch"
                    value={formData.branch}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                    disabled={editMode}
                  >
                    {!editMode && <option value="all">All Branches</option>}
                    <option value="Lehulu">Lehulu</option>
                    <option value="Kalkidan">Kalkidan</option>
                    <option value="Store">Store</option>
                  </select>
                </div>
                {formData.branch === "Store" && (
                  <div>
                    <label
                      htmlFor="reserve_for_branch"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Reserve for Branch
                    </label>
                    <select
                      id="reserve_for_branch"
                      name="reserve_for_branch"
                      value={formData.reserve_for_branch}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">Select Branch</option>
                      <option value="Lehulu">Lehulu</option>
                      <option value="Kalkidan">Kalkidan</option>
                    </select>
                  </div>
                )}
                {["goma", "battery", "flop", "kelemendary"].indexOf(
                  categories
                    .find((cat) => cat.id === parseInt(formData.categoryId))
                    ?.name?.toLowerCase()
                ) === -1 && (
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Item Name*
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                )}
                <div>
                  <label
                    htmlFor="brand"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Brand
                  </label>
                  <input
                    type="text"
                    id="brand"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                {categories
                  .find((cat) => cat.id === parseInt(formData.categoryId))
                  ?.name?.toLowerCase() === "battery" ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Size*
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="size"
                          value="inside"
                          checked={formData.size === "inside"}
                          onChange={handleInputChange}
                          className="mr-2"
                          required
                        />
                        Inside
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="size"
                          value="outside"
                          checked={formData.size === "outside"}
                          onChange={handleInputChange}
                          className="mr-2"
                          required
                        />
                        Outside
                      </label>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label
                      htmlFor="size"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Size
                    </label>
                    <input
                      type="text"
                      id="size"
                      name="size"
                      value={formData.size}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                )}
                {["battery", "flop", "kelemendary"].indexOf(
                  categories
                    .find((cat) => cat.id === parseInt(formData.categoryId))
                    ?.name?.toLowerCase()
                ) === -1 && (
                  <div>
                    <label
                      htmlFor="uniquesize"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Unique Size
                    </label>
                    <input
                      type="text"
                      id="uniquesize"
                      name="uniquesize"
                      value={formData.uniquesize}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                )}
                <div>
                  <label
                    htmlFor="origin"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Origin*
                  </label>
                  <input
                    type="text"
                    id="origin"
                    name="origin"
                    value={formData.origin}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>
                {categories
                  .find((cat) => cat.id === parseInt(formData.categoryId))
                  ?.name?.toLowerCase() === "battery" && (
                  <>
                    <div>
                      <label
                        htmlFor="ampere"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Ampere*
                      </label>
                      <input
                        type="text"
                        id="ampere"
                        name="ampere"
                        value={formData.ampere}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Position*
                      </label>
                      <div className="flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="position"
                            value="left"
                            checked={formData.position === "left"}
                            onChange={handleInputChange}
                            className="mr-2"
                            required
                          />
                          Left
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="position"
                            value="right"
                            checked={formData.position === "right"}
                            onChange={handleInputChange}
                            className="mr-2"
                            required
                          />
                          Right
                        </label>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 flex items-center justify-center min-w-24"
                  disabled={buttonLoading.submit}
                >
                  {buttonLoading.submit ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {editMode ? "Updating..." : "Adding..."}
                    </>
                  ) : editMode ? (
                    "Update Item"
                  ) : (
                    "Add Item"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    if (!editMode) resetForm();
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {isViewModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-700">Item Details</h2>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="space-y-3">
              {selectedItem.category?.name && (
                <div>
                  <span className="font-medium text-gray-700">Category: </span>
                  <span className="text-gray-600">{selectedItem.category.name}</span>
                </div>
              )}
              {selectedItem.name && (
                <div>
                  <span className="font-medium text-gray-700">Name: </span>
                  <span className="text-gray-600">{selectedItem.name}</span>
                </div>
              )}
              {selectedItem.brand && (
                <div>
                  <span className="font-medium text-gray-700">Brand: </span>
                  <span className="text-gray-600">{selectedItem.brand}</span>
                </div>
              )}
              {selectedItem.size && (
                <div>
                  <span className="font-medium text-gray-700">Size: </span>
                  <span className="text-gray-600">{selectedItem.size}</span>
                </div>
              )}
              {selectedItem.uniquesize && (
                <div>
                  <span className="font-medium text-gray-700">Unique Size: </span>
                  <span className="text-gray-600">{selectedItem.uniquesize}</span>
                </div>
              )}
              {selectedItem.ampere && (
                <div>
                  <span className="font-medium text-gray-700">Ampere: </span>
                  <span className="text-gray-600">{selectedItem.ampere}</span>
                </div>
              )}
              {selectedItem.position && (
                <div>
                  <span className="font-medium text-gray-700">Position: </span>
                  <span className="text-gray-600">{selectedItem.position}</span>
                </div>
              )}
              {selectedItem.origin && (
                <div>
                  <span className="font-medium text-gray-700">Origin: </span>
                  <span className="text-gray-600">{selectedItem.origin}</span>
                </div>
              )}
              {selectedItem.branch && (
                <div>
                  <span className="font-medium text-gray-700">Branch: </span>
                  <span className="text-gray-600">{selectedItem.branch}</span>
                </div>
              )}
              {selectedItem.reserve_for_branch && (
                <div>
                  <span className="font-medium text-gray-700">Reserve for Branch: </span>
                  <span className="text-gray-600">{selectedItem.reserve_for_branch}</span>
                </div>
              )}
              {selectedItem.quantity !== undefined && (
                <div>
                  <span className="font-medium text-gray-700">Quantity: </span>
                  <span className="text-gray-600">{selectedItem.quantity}</span>
                </div>
              )}
              {selectedItem.created_at && (
                <div>
                  <span className="font-medium text-gray-700">Created At: </span>
                  <span className="text-gray-600">
                    {new Date(selectedItem.created_at).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
      />
    </div>
  );
};

export default ItemManagement;